import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FileService, FilesResponse } from '../../../services/file.service';
import { BreadcrumbComponent, BreadcrumbScope } from '../breadcrumb/breadcrumb.component';

type CardVariant = 'row' | 'grid' | 'changelog';

@Component({
  selector: 'app-post-card',
  standalone: true,
  imports: [CommonModule, BreadcrumbComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <article class="card"
             [class.card--row]="variant === 'row'"
             [class.card--grid]="variant === 'grid'"
             [class.card--changelog]="variant === 'changelog'"
             [class.card--active]="isActive">
      <button type="button" class="card__main" (click)="activate.emit(bundle.id)" [attr.aria-expanded]="isActive">
        <div class="card__thumbWrap">
          <img *ngIf="thumbUrl"
               class="card__thumb"
               [src]="thumbUrl"
               [alt]="bundle.topic || ''"
               loading="lazy"
               decoding="async"
               fetchpriority="low">
          <div *ngIf="!thumbUrl" class="card__thumb card__thumb--placeholder" aria-hidden="true">
            <span class="material-icons">{{ placeholderIcon() }}</span>
          </div>
          <div class="card__thumbOverlay" aria-hidden="true">
            <span class="card__formatBadge" *ngIf="hasFormat('html')"><span class="material-icons">account_tree</span></span>
            <span class="card__formatBadge" *ngIf="hasFormat('pdf')"><span class="material-icons">picture_as_pdf</span></span>
            <span class="card__formatBadge" *ngIf="hasFormat('youtube')"><span class="material-icons">play_circle</span></span>
          </div>
        </div>

        <div class="card__body">
          <app-breadcrumb [bundle]="bundle" (navigate)="onCrumb($event)"></app-breadcrumb>
          <h3 class="card__title">{{ bundle.topic || bundle.subtopic || 'Untitled' }}</h3>
          <p class="card__excerpt" *ngIf="excerpt">{{ excerpt }}</p>
          <div class="card__meta">
            <span class="card__lang">
              <span class="lang lang--en">EN</span>
              <span class="lang lang--hi" *ngIf="hasHindi">हिं</span>
            </span>
            <span class="card__dot" aria-hidden="true">·</span>
            <span class="card__date" *ngIf="dateLabel">{{ dateLabel }}</span>
            <span class="card__formats">
              <span *ngIf="hasFormat('html')"   class="material-icons" title="Mindmap">account_tree</span>
              <span *ngIf="hasFormat('pdf')"    class="material-icons" title="PDF">picture_as_pdf</span>
              <span *ngIf="hasFormat('png')"    class="material-icons" title="Image">image</span>
              <span *ngIf="hasFormat('youtube')" class="material-icons" title="Video">play_circle</span>
            </span>
          </div>
        </div>

        <span class="card__chev material-icons" aria-hidden="true">{{ isActive ? 'expand_less' : 'arrow_forward' }}</span>
      </button>
    </article>
  `,
  styleUrls: ['./post-card.component.css'],
})
export class PostCardComponent {
  @Input({ required: true }) bundle!: FilesResponse;
  @Input() isActive = false;
  @Input() variant: CardVariant = 'row';
  @Output() activate = new EventEmitter<number>();
  @Output() crumbNavigate = new EventEmitter<{ scope: BreadcrumbScope; bundle: FilesResponse }>();

  private fileService = inject(FileService);

  get thumbUrl(): string | null { return this.fileService.getThumbnailUrl(this.bundle); }
  get hasHindi(): boolean { return this.fileService.bundleHasHindi(this.bundle); }
  get excerpt(): string | null {
    return this.bundle.subtopic && this.bundle.subtopic !== this.bundle.topic
      ? this.bundle.subtopic
      : null;
  }

  get dateLabel(): string | null {
    const d = this.bundle.files?.['html']?.created_at ?? this.bundle.files?.['pdf']?.created_at;
    if (!d) return null;
    const date = new Date(d);
    if (isNaN(date.getTime())) return null;
    const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (days <= 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7)   return `${days} days ago`;
    if (days < 30)  return `${Math.floor(days / 7)} week${Math.floor(days / 7) > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  hasFormat(t: 'html' | 'pdf' | 'png' | 'youtube'): boolean {
    if (t === 'youtube') return !!(this.bundle.youtube?.has_video);
    return !!this.bundle.files?.[t]?.filename;
  }

  placeholderIcon(): string {
    const subj = (this.bundle.subject ?? '').toLowerCase();
    if (subj.includes('history'))   return 'history_edu';
    if (subj.includes('geography')) return 'public';
    if (subj.includes('science'))   return 'science';
    if (subj.includes('economy') || subj.includes('econ')) return 'trending_up';
    if (subj.includes('polity'))    return 'gavel';
    return 'account_tree';
  }

  onCrumb(evt: { scope: BreadcrumbScope; bundle: FilesResponse }): void {
    this.crumbNavigate.emit(evt);
  }
}
